import { useEffect } from 'react';

import { trpc } from '@/api/tRpc';

/**
 * Hook to fetch paginated list of mappings with optional filtering.
 *
 * @param { object } params Optional pagination and filter parameters.
 * @param { number } [params.limit] Maximum number of mappings to fetch.
 * @param { number } [params.offset] Number of mappings to skip.
 * @param { boolean } [params.activeOnly] Whether to fetch only active mappings.
 * @param { object } options Optional query options, for callers that must defer or cache the fetch.
 * @param { boolean } [options.enabled] Whether the query should run.
 * @param { number } [options.staleTime] How long the result stays fresh, in ms.
 * @returns { object } Returns tRPC query result with mappings list.
 */
export const useMappings = (
  params?: { limit?: number; offset?: number; activeOnly?: boolean },
  options?: { enabled?: boolean; staleTime?: number },
) =>
  trpc.mappings.list.useQuery(
    {
      limit: params?.limit,
      offset: params?.offset,
      activeOnly: params?.activeOnly,
    },
    options,
  );

/**
 * Hook to fetch a summary of every saved mapping: id, mapping key, adapter configuration, target table
 * and active state.
 *
 * Unpaginated on purpose. Callers deciding something per saved mapping cannot use a page of
 * `useMappings`: a key missing from a truncated page reads as "no such mapping", which is how a
 * bulk import could silently deactivate one and drop its table from the CDC publication.
 *
 * One table set can have several mappings, one per adapter configuration, so a mapping key alone does
 * not identify a saved mapping.
 *
 * @param { object } options Optional query options, for callers that must defer or cache the fetch.
 * @param { boolean } [options.enabled] Whether the query should run.
 * @param { number } [options.staleTime] How long the result stays fresh, in ms.
 * @returns { object } Returns tRPC query result with one entry per mapping.
 */
export const useMappingSummaries = (options?: { enabled?: boolean; staleTime?: number }) =>
  trpc.mappings.summaries.useQuery(undefined, options);

/** Upper bound for mapping fetch — tenants exceeding this will log a warning */
export const MAPPED_TABLES_FETCH_LIMIT = 1000;

/**
 * Warns when a capped mappings fetch did not return every mapping.
 *
 * Anything derived from a truncated page is incomplete — a table can look unmapped, or an export
 * count can under-report — so callers say what their own consequence is rather than truncating
 * silently.
 *
 * @param { string } label The calling hook or component, used to prefix the warning.
 * @param { string } consequence What is inaccurate while the fetch is truncated.
 * @param { object } data The mappings query result, or undefined while it is in flight.
 * @param { number } [data.total] Total mappings available for the retailer/environment.
 * @param { unknown[] } [data.mappings] The mappings actually fetched.
 */
export const useFetchOverflowWarning = (
  label: string,
  consequence: string,
  data: { total?: number; mappings?: unknown[] } | undefined,
): void => {
  const fetched = data?.mappings?.length ?? 0;
  const total = data?.total ?? 0;
  const isOverflow = total > fetched;

  useEffect(() => {
    if (isOverflow) {
      console.warn(`[${label}] Fetched ${fetched} of ${total} mappings — ${consequence}`);
    }
  }, [label, total, fetched, isOverflow, consequence]);
};

/**
 * Hook to fetch a single mapping by its id.
 *
 * @param { number | undefined } id The mapping id; the query does not run without a positive integer id.
 * @returns { object } Returns tRPC query result with mapping details.
 */
export const useMapping = (id: number | undefined) =>
  trpc.mappings.get.useQuery({ id: id ?? 0 }, { enabled: Number.isInteger(id) && (id ?? 0) > 0 });

/**
 * Invalidates every mapping read after a write.
 *
 * Add new `mappings` queries here, not at each mutation. A query missing from one call site is how
 * the bulk-import conflict check came to compare against pre-write state, which let an import
 * deactivate a mapping and drop its table from the CDC publication unchallenged.
 *
 * @param { ReturnType<typeof trpc.useUtils> } utils The tRPC utils proxy from `trpc.useUtils()`.
 */
export const invalidateMappingReads = (utils: ReturnType<typeof trpc.useUtils>): void => {
  utils.mappings.list.invalidate();
  utils.mappings.get.invalidate();
  utils.mappings.summaries.invalidate();
};

/**
 * Hook to create a new mapping.
 *
 * @returns { object } Returns mutation object for creating mappings.
 */
export const useCreateMapping = () => {
  const utils = trpc.useUtils();

  return trpc.mappings.create.useMutation({
    onSuccess: () => {
      invalidateMappingReads(utils);
    },
  });
};

/**
 * Hook to update an existing mapping.
 *
 * @returns { object } Returns mutation object for updating mappings.
 */
export const useUpdateMapping = () => {
  const utils = trpc.useUtils();

  return trpc.mappings.update.useMutation({
    onSuccess: () => {
      invalidateMappingReads(utils);
    },
  });
};

/**
 * Hook to delete a mapping.
 *
 * @returns { object } Returns mutation object for deleting mappings.
 */
export const useDeleteMapping = () => {
  const utils = trpc.useUtils();

  return trpc.mappings.delete.useMutation({
    onSuccess: () => {
      invalidateMappingReads(utils);
    },
  });
};

/**
 * Hook to toggle mapping active/inactive status.
 *
 * @returns { object } Returns mutation object for toggling mapping status.
 */
export const useToggleMappingActive = () => {
  const utils = trpc.useUtils();

  return trpc.mappings.toggleActive.useMutation({
    onSuccess: () => {
      invalidateMappingReads(utils);
    },
  });
};

/**
 * Hook to bulk import multiple mappings.
 *
 * @returns { object } Returns mutation object for bulk importing mappings.
 */
export const useBulkImportMappings = () => {
  const utils = trpc.useUtils();

  return trpc.mappings.bulkImport.useMutation({
    onSuccess: () => {
      invalidateMappingReads(utils);
    },
  });
};
